import React, { useRef, useState } from 'react';
import { isNative, takePhotoNative, requestCameraPermissions } from '../../../hooks/useNativeCamera';
import { Camera, Upload, X, Loader2, Sparkles, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../utils/api';

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Claude (Anthropic)',
  openai:    'GPT-4o (OpenAI)',
  gemini:    'Gemini (Google)',
};

export interface ParsedReceipt {
  supplier_name: string | null;
  invoice_number: string | null;
  purchased_at: string;
  expense_category: string;
  items: {
    name: string;
    quantity: number;
    unit: string;
    unit_price: number;
  }[];
  subtotal: number;
  tax: number;
  total: number;
  notes: string | null;
  confidence: 'alta' | 'media' | 'baja';
  extraction_notes: string | null;
  error: string | null;
}

interface ReceiptScannerProps {
  onParsed: (data: ParsedReceipt) => void;
  onClose: () => void;
}

const CONFIDENCE_STYLES = {
  alta:  { color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2 },
  media: { color: 'text-amber-600',   bg: 'bg-amber-50 border-amber-200',     icon: AlertCircle },
  baja:  { color: 'text-red-600',     bg: 'bg-red-50 border-red-200',         icon: AlertCircle },
};

const CATEGORY_LABELS: Record<string, string> = {
  INSUMOS:           '🥩 Insumos / ingredientes',
  SERVICIOS:         '🔧 Servicios externos',
  NOMINA:            '👥 Nómina / personal',
  MANTENIMIENTO:     '⚙️ Mantenimiento / equipos',
  ARRIENDO:          '🏠 Arriendo',
  SERVICIOS_PUBLICOS:'💡 Servicios públicos',
  OTROS:             '📦 Otros',
};

export function ReceiptScanner({ onParsed, onClose }: ReceiptScannerProps) {
  const { data: aiConfig } = useQuery({
    queryKey: ['ai-providers'],
    queryFn: async () => {
      const { data } = await api.get('/purchases/ai-providers');
      return data as { active: string };
    },
    staleTime: 60_000,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ParsedReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Solo se permiten imágenes (JPG, PNG, WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('La imagen no puede superar 10 MB.');
      return;
    }
    setImageFile(file);
    setResult(null);
    setError(null);

    const reader = new FileReader();
    reader.onload = e => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const analyze = async () => {
    if (!preview) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data } = await api.post<ParsedReceipt>('/purchases/parse-receipt', {
        base64: preview, // data-URL format
      });
      setResult(data);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? 'Error al analizar el recibo.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUse = () => {
    if (result) onParsed(result);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={18} className="text-violet-500 shrink-0" />
            <div>
              <h2 className="font-semibold text-gray-900 leading-tight">Escanear recibo con IA</h2>
              {aiConfig && (
                <p className="text-[10px] text-gray-400 leading-none mt-0.5">
                  {PROVIDER_LABELS[aiConfig.active] ?? aiConfig.active}
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Upload zone */}
          {!preview ? (
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              className={`flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed p-8 transition-colors cursor-pointer ${
                dragOver ? 'border-violet-400 bg-violet-50' : 'border-gray-200 hover:border-violet-300 hover:bg-violet-50/50'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-violet-100">
                <Upload size={24} className="text-violet-600" />
              </div>
              <div className="text-center">
                <p className="font-medium text-gray-700">Arrastra la foto aquí</p>
                <p className="text-sm text-gray-400 mt-1">o haz clic para seleccionar</p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium hover:bg-gray-50 transition"
                >
                  <Upload size={14} /> Desde archivo
                </button>
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); cameraInputRef.current?.click(); }}
                  className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 transition"
                >
                  <Camera size={14} /> Tomar foto
                </button>
              </div>
              <p className="text-xs text-gray-400">JPG, PNG, WebP · máx. 10 MB</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Image preview */}
              <div className="relative rounded-xl overflow-hidden border border-gray-200">
                <img src={preview} alt="Recibo" className="w-full max-h-60 object-contain bg-gray-50" />
                <button
                  onClick={() => { setPreview(null); setImageFile(null); setResult(null); setError(null); }}
                  className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Analyze button */}
              {!result && (
                <button
                  onClick={analyze}
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 py-3 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60 transition"
                >
                  {loading
                    ? <><Loader2 size={16} className="animate-spin" /> Analizando con IA…</>
                    : <><Sparkles size={16} /> Analizar recibo</>}
                </button>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-3">
              {/* Confidence badge */}
              {(() => {
                const conf = result.confidence as 'alta' | 'media' | 'baja';
                const style = CONFIDENCE_STYLES[conf] ?? CONFIDENCE_STYLES.baja;
                const Icon = style.icon;
                return (
                  <div className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${style.bg} ${style.color}`}>
                    <Icon size={16} className="shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold">Confianza: {conf}</span>
                      {result.extraction_notes && <p className="mt-0.5 text-xs opacity-80">{result.extraction_notes}</p>}
                    </div>
                  </div>
                );
              })()}

              {/* Extracted data summary */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 divide-y divide-gray-100 text-sm">
                {result.supplier_name && (
                  <Row label="Proveedor" value={result.supplier_name} />
                )}
                {result.invoice_number && (
                  <Row label="Factura #" value={result.invoice_number} />
                )}
                <Row label="Fecha" value={result.purchased_at} />
                <Row label="Categoría" value={CATEGORY_LABELS[result.expense_category] ?? result.expense_category} />
                <Row label="Ítems" value={`${result.items.length} producto${result.items.length !== 1 ? 's' : ''}`} />
                {result.tax > 0 && <Row label="IVA" value={fmt(result.tax)} />}
                <Row label="Total" value={fmt(result.total)} bold />
              </div>

              {result.items.length > 0 && (
                <div className="rounded-xl border border-gray-200 overflow-hidden text-xs">
                  <div className="bg-gray-100 px-3 py-2 font-semibold text-gray-600">Ítems detectados</div>
                  {result.items.map((item, i) => (
                    <div key={i} className="flex justify-between items-center px-3 py-2 border-t border-gray-100">
                      <span className="text-gray-700">{item.quantity} {item.unit} × {item.name}</span>
                      <span className="font-medium text-gray-900">{fmt(item.unit_price)}</span>
                    </div>
                  ))}
                </div>
              )}

              {result.error && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  ⚠️ Algunos datos pueden ser inexactos. Revisa el formulario antes de guardar.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-5 py-4 flex gap-3 shrink-0">
          {result ? (
            <>
              <button
                onClick={handleUse}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 transition"
              >
                <CheckCircle2 size={16} /> Usar estos datos
              </button>
              <button
                onClick={() => { setResult(null); setPreview(null); setImageFile(null); }}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium hover:bg-gray-50 transition"
              >
                Nuevo escaneo
              </button>
            </>
          ) : (
            <button onClick={onClose} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium hover:bg-gray-50 transition">
              Cancelar
            </button>
          )}
        </div>

        {/* Hidden file inputs */}
        <input ref={fileInputRef} type="file" accept="image/*" className="sr-only" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="sr-only" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
      </div>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between px-3 py-2">
      <span className="text-gray-500">{label}</span>
      <span className={`text-right ${bold ? 'font-bold text-gray-900' : 'text-gray-700'}`}>{value}</span>
    </div>
  );
}

function fmt(n: number) {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
}

export default ReceiptScanner;
